import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  FileText, Plus, Search, Filter, Calendar, AlertTriangle, CheckCircle,
  Clock, User, Edit3, Eye, Download, Target, ChevronDown, ChevronRight,
  Shield, Bell, RefreshCw, Activity, Heart, Brain, BarChart3, Award,
  Clipboard, Lock, Unlock, ArrowRight, XCircle, Save, X, Loader2,
  AlertCircle, TrendingUp, TrendingDown, Minus, List, ClipboardCheck,
  Users, MessageSquare, FileCheck, Stethoscope, Pill, Coffee,
  HandHeart, Ear, Smile, Moon, Utensils, Droplets, Footprints
} from 'lucide-react';
import CarePlanService from '../../services/clinical/CarePlanService';
import {
  CARE_PLAN_STATUS, CARE_PLAN_TYPE, CARE_PLAN_SECTIONS, SECTION_STATUS,
  RISK_LEVEL, GOAL_STATUS, INTERVENTION_STATUS, REVIEW_OUTCOME,
  createCarePlanGoal, createCarePlanIntervention, createCarePlanNeed,
} from '../../data/models/CarePlan';
import { getInterventionsForSection } from '../../data/interventionLibrary';
import { seedCarePlans } from '../../data/carePlanSeedData';

const isDev = process.env.NODE_ENV === 'development';
const log = (...a) => isDev && console.debug('[CarePlans]', ...a); // eslint-disable-line no-console

const SECTION_ICONS = {
  1: <HandHeart className="w-4 h-4" />, 2: <Stethoscope className="w-4 h-4" />,
  3: <Coffee className="w-4 h-4" />, 4: <Footprints className="w-4 h-4" />,
  5: <Shield className="w-4 h-4" />, 6: <Activity className="w-4 h-4" />,
  7: <Pill className="w-4 h-4" />, 8: <Utensils className="w-4 h-4" />,
  9: <Heart className="w-4 h-4" />, 10: <MessageSquare className="w-4 h-4" />,
  11: <Brain className="w-4 h-4" />, 12: <Smile className="w-4 h-4" />,
  13: <AlertCircle className="w-4 h-4" />, 14: <Droplets className="w-4 h-4" />,
  15: <ClipboardCheck className="w-4 h-4" />, 16: <Ear className="w-4 h-4" />,
  17: <FileCheck className="w-4 h-4" />, 18: <Moon className="w-4 h-4" />,
};

const STATUS_COLORS = {
  [CARE_PLAN_STATUS.DRAFT]: 'bg-yellow-100 text-yellow-800',
  [CARE_PLAN_STATUS.ACTIVE]: 'bg-green-100 text-green-800',
  [CARE_PLAN_STATUS.UNDER_REVIEW]: 'bg-blue-100 text-blue-800',
  [CARE_PLAN_STATUS.SUPERSEDED]: 'bg-gray-100 text-gray-600',
  [CARE_PLAN_STATUS.ARCHIVED]: 'bg-gray-100 text-gray-500',
};

const TYPE_LABELS = {
  [CARE_PLAN_TYPE.COMPREHENSIVE]: 'Comprehensive',
  [CARE_PLAN_TYPE.SUMMARY]: 'Summary',
  [CARE_PLAN_TYPE.BEHAVIOUR_SUPPORT]: 'Behaviour Support',
  [CARE_PLAN_TYPE.PALLIATIVE]: 'Palliative',
  [CARE_PLAN_TYPE.INTERIM]: 'Interim',
};

const TYPE_COLORS = {
  [CARE_PLAN_TYPE.COMPREHENSIVE]: 'border-blue-500',
  [CARE_PLAN_TYPE.SUMMARY]: 'border-teal-500',
  [CARE_PLAN_TYPE.BEHAVIOUR_SUPPORT]: 'border-orange-500',
  [CARE_PLAN_TYPE.PALLIATIVE]: 'border-purple-500',
  [CARE_PLAN_TYPE.INTERIM]: 'border-yellow-500',
};

const RISK_COLORS = {
  [RISK_LEVEL.LOW]: 'bg-green-100 text-green-800',
  [RISK_LEVEL.MEDIUM]: 'bg-yellow-100 text-yellow-800',
  [RISK_LEVEL.HIGH]: 'bg-orange-100 text-orange-800',
  [RISK_LEVEL.VERY_HIGH]: 'bg-red-100 text-red-800',
};

const GOAL_STATUS_COLORS = {
  [GOAL_STATUS.NOT_STARTED]: 'bg-gray-100 text-gray-700',
  [GOAL_STATUS.IN_PROGRESS]: 'bg-blue-100 text-blue-800',
  [GOAL_STATUS.ACHIEVED]: 'bg-green-100 text-green-800',
  [GOAL_STATUS.PARTIALLY_ACHIEVED]: 'bg-yellow-100 text-yellow-800',
  [GOAL_STATUS.NOT_ACHIEVED]: 'bg-red-100 text-red-800',
  [GOAL_STATUS.MODIFIED]: 'bg-purple-100 text-purple-800',
  [GOAL_STATUS.DISCONTINUED]: 'bg-gray-200 text-gray-600',
};

// ── SectionEditor sub-component ────────────────────────────────────────

function SectionEditor({ section, carePlanId, onSave, onCancel, readOnly }) {
  const [notes, setNotes] = useState(section.additionalNotes || '');
  const [preferences, setPreferences] = useState(section.residentPreferences || '');
  const [familyInput, setFamilyInput] = useState(section.familyInput || '');
  const [newNeed, setNewNeed] = useState('');
  const [newGoalText, setNewGoalText] = useState('');
  const [newInterventionText, setNewInterventionText] = useState('');
  const [saving, setSaving] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);

  const libraryInterventions = useMemo(
    () => getInterventionsForSection(section.sectionKey, { riskLevel: section.riskLevel }),
    [section.sectionKey, section.riskLevel]
  );

  const handleSave = async () => {
    setSaving(true);
    try {
      const update = { additionalNotes: notes, residentPreferences: preferences, familyInput };
      const result = CarePlanService.updateSection(carePlanId, section.sectionNumber, update, 'current-user');
      if (result.ok) {
        log('Section saved', section.sectionNumber);
        onSave(result.carePlan);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleAddNeed = () => {
    if (!newNeed.trim()) return;
    const result = CarePlanService.addNeed(carePlanId, section.sectionNumber, { description: newNeed.trim(), source: 'clinical_observation' });
    if (result.ok) { onSave(result.carePlan); setNewNeed(''); }
  };

  const handleAddGoal = () => {
    if (!newGoalText.trim()) return;
    const result = CarePlanService.addGoal(carePlanId, section.sectionNumber, { goalText: newGoalText.trim() });
    if (result.ok) { onSave(result.carePlan); setNewGoalText(''); }
  };

  const handleAddIntervention = () => {
    if (!newInterventionText.trim()) return;
    const result = CarePlanService.addIntervention(carePlanId, section.sectionNumber, { interventionText: newInterventionText.trim(), source: 'manual' });
    if (result.ok) { onSave(result.carePlan); setNewInterventionText(''); }
  };

  const handleAddFromLibrary = (libEntry) => {
    const result = CarePlanService.addIntervention(carePlanId, section.sectionNumber, {
      interventionText: libEntry.interventionText,
      category: libEntry.category,
      frequency: libEntry.frequency,
      responsibleRole: libEntry.responsibleRole,
      source: 'intervention_library',
      sourceInterventionId: libEntry.id,
      evidenceBasis: libEntry.evidenceBasis,
      isHighlighted: libEntry.isDefault,
    });
    if (result.ok) onSave(result.carePlan);
  };

  return (
    <div className="space-y-4">
      {/* Clinical alerts */}
      {section.clinicalAlerts?.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <h5 className="font-semibold text-red-800 text-sm mb-1">Clinical Alerts</h5>
          {section.clinicalAlerts.map((alert, i) => (
            <div key={i} className="flex items-center gap-2 text-sm text-red-700">
              <AlertTriangle className="w-3 h-3 flex-shrink-0" />{alert}
            </div>
          ))}
        </div>
      )}

      {/* Needs */}
      <div>
        <h5 className="font-semibold text-gray-700 text-sm mb-2">Identified Needs ({section.needs?.length || 0})</h5>
        {(section.needs || []).map((need, i) => (
          <div key={need.id || i} className="flex items-start gap-2 text-sm mb-1 p-2 bg-gray-50 rounded">
            <span className={`px-1.5 py-0.5 rounded text-xs ${need.priority === 'high' || need.priority === 'critical' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
              {need.priority || 'medium'}
            </span>
            <span className="flex-1">{need.description || need}</span>
          </div>
        ))}
        {!readOnly && (
          <div className="flex gap-2 mt-2">
            <input value={newNeed} onChange={e => setNewNeed(e.target.value)} placeholder="Add new need..."
              className="flex-1 text-sm px-3 py-1.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              onKeyDown={e => e.key === 'Enter' && handleAddNeed()} />
            <button onClick={handleAddNeed} className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">Add</button>
          </div>
        )}
      </div>

      {/* Goals */}
      <div>
        <h5 className="font-semibold text-gray-700 text-sm mb-2">Goals ({section.goals?.length || 0})</h5>
        {(section.goals || []).map((goal, i) => (
          <div key={goal.id || i} className="p-2 bg-gray-50 rounded mb-1">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1">
                <p className="text-sm">{goal.goalText || goal}</p>
                {goal.residentVoice && <p className="text-xs text-purple-600 mt-1 italic">Resident: "{goal.residentVoice}"</p>}
              </div>
              <span className={`px-2 py-0.5 rounded text-xs whitespace-nowrap ${GOAL_STATUS_COLORS[goal.status] || 'bg-gray-100'}`}>
                {(goal.status || 'not_started').replace(/_/g, ' ')}
              </span>
            </div>
            {goal.progressEntries?.length > 0 && (
              <div className="mt-1 text-xs text-gray-500">{goal.progressEntries.length} progress entries</div>
            )}
          </div>
        ))}
        {!readOnly && (
          <div className="flex gap-2 mt-2">
            <input value={newGoalText} onChange={e => setNewGoalText(e.target.value)} placeholder="Add new goal..."
              className="flex-1 text-sm px-3 py-1.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              onKeyDown={e => e.key === 'Enter' && handleAddGoal()} />
            <button onClick={handleAddGoal} className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700">Add</button>
          </div>
        )}
      </div>

      {/* Interventions */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h5 className="font-semibold text-gray-700 text-sm">Interventions ({section.interventions?.length || 0})</h5>
          {!readOnly && libraryInterventions.length > 0 && (
            <button onClick={() => setShowLibrary(!showLibrary)}
              className="text-xs text-purple-600 hover:text-purple-800 flex items-center gap-1">
              <List className="w-3 h-3" />{showLibrary ? 'Hide' : 'Browse'} Library ({libraryInterventions.length})
            </button>
          )}
        </div>
        {(section.interventions || []).map((intv, i) => (
          <div key={intv.id || i} className="flex items-start gap-2 text-sm mb-1 p-2 bg-gray-50 rounded">
            <CheckCircle className={`w-3 h-3 mt-0.5 flex-shrink-0 ${intv.status === 'discontinued' ? 'text-gray-400' : 'text-green-500'}`} />
            <div className="flex-1">
              <span className={intv.status === 'discontinued' ? 'line-through text-gray-400' : ''}>{intv.interventionText || intv}</span>
              <div className="flex gap-2 mt-0.5 text-xs text-gray-500">
                {intv.frequency && <span>{intv.frequency}</span>}
                {intv.responsibleRole && <span>• {intv.responsibleRole}</span>}
                {intv.source === 'intervention_library' && <span className="text-purple-500">• Library</span>}
              </div>
            </div>
            {intv.isHighlighted && <span className="text-xs bg-yellow-100 text-yellow-700 px-1.5 rounded">Key</span>}
          </div>
        ))}
        {showLibrary && (
          <div className="mt-2 border border-purple-200 rounded-lg p-3 bg-purple-50">
            <h6 className="text-xs font-semibold text-purple-800 mb-2">Intervention Library — {section.title}</h6>
            {libraryInterventions.map(lib => {
              const alreadyAdded = (section.interventions || []).some(i => i.interventionText === lib.interventionText);
              return (
                <div key={lib.id} className="flex items-start justify-between gap-2 text-xs mb-2 p-2 bg-white rounded">
                  <div className="flex-1">
                    <p>{lib.interventionText}</p>
                    <span className="text-gray-500">{lib.frequency} • {lib.responsibleRole}</span>
                  </div>
                  {alreadyAdded ? (
                    <span className="text-green-600 text-xs flex-shrink-0">Added</span>
                  ) : (
                    <button onClick={() => handleAddFromLibrary(lib)} className="text-purple-600 hover:text-purple-800 flex-shrink-0">
                      <Plus className="w-4 h-4" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {!readOnly && (
          <div className="flex gap-2 mt-2">
            <input value={newInterventionText} onChange={e => setNewInterventionText(e.target.value)} placeholder="Add custom intervention..."
              className="flex-1 text-sm px-3 py-1.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              onKeyDown={e => e.key === 'Enter' && handleAddIntervention()} />
            <button onClick={handleAddIntervention} className="px-3 py-1.5 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700">Add</button>
          </div>
        )}
      </div>

      {/* Notes */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div>
          <label className="text-xs font-medium text-gray-600">Clinical Notes</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} disabled={readOnly} rows={3}
            className="w-full mt-1 text-sm px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50" />
        </div>
        <div>
          <label className="text-xs font-medium text-gray-600">Resident Preferences</label>
          <textarea value={preferences} onChange={e => setPreferences(e.target.value)} disabled={readOnly} rows={3}
            className="w-full mt-1 text-sm px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50" />
        </div>
        <div>
          <label className="text-xs font-medium text-gray-600">Family Input</label>
          <textarea value={familyInput} onChange={e => setFamilyInput(e.target.value)} disabled={readOnly} rows={3}
            className="w-full mt-1 text-sm px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50" />
        </div>
      </div>

      {/* Assessment sources */}
      {section.assessmentSources?.length > 0 && (
        <div className="text-xs text-gray-500">
          <span className="font-medium">Sources:</span> {section.assessmentSources.map(s => s.name || s.slug).join(', ')}
        </div>
      )}

      {!readOnly && (
        <div className="flex justify-end gap-2 pt-2 border-t">
          <button onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Section
          </button>
        </div>
      )}
    </div>
  );
}

// ── SummaryView sub-component (point-of-care) ──────────────────────────

function SummaryView({ summary }) {
  if (!summary) return <div className="text-center text-gray-500 py-8">No summary available</div>;

  const SummaryRow = ({ icon, label, children }) => (
    <div className="flex items-start gap-3 py-3 border-b border-gray-100 last:border-0">
      <div className="mt-0.5 text-blue-600">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{label}</div>
        <div className="text-sm text-gray-800">{children}</div>
      </div>
    </div>
  );

  return (
    <div className="bg-white rounded-xl border-2 border-blue-200 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 p-4 text-white">
        <div className="flex items-center gap-3">
          {summary.photo && <img src={summary.photo} alt="" className="w-12 h-12 rounded-full border-2 border-white" />}
          <div>
            <h3 className="text-lg font-bold">{summary.residentName || 'Resident'} {summary.preferredName ? `(${summary.preferredName})` : ''}</h3>
            <p className="text-blue-100 text-sm">{summary.room} {summary.ward ? `· ${summary.ward}` : ''}</p>
          </div>
        </div>
      </div>

      {/* Alerts banner */}
      {summary.alerts?.length > 0 && (
        <div className="bg-red-50 px-4 py-2 border-b border-red-200">
          <div className="flex items-center gap-2 flex-wrap">
            <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0" />
            {summary.alerts.map((alert, i) => (
              <span key={i} className="px-2 py-0.5 bg-red-100 text-red-800 text-xs font-semibold rounded">{alert}</span>
            ))}
          </div>
        </div>
      )}

      <div className="p-4">
        <SummaryRow icon={<Utensils className="w-4 h-4" />} label="Diet">
          {summary.diet?.summary || 'Standard diet'}
          {summary.diet?.needs?.length > 0 && <div className="text-xs text-gray-500 mt-1">{summary.diet.needs.join(' · ')}</div>}
        </SummaryRow>

        <SummaryRow icon={<Footprints className="w-4 h-4" />} label="Mobility">
          {summary.mobility?.summary || 'See care plan'}
          {summary.mobility?.interventions?.length > 0 && (
            <ul className="mt-1 text-xs text-gray-600">{summary.mobility.interventions.map((i, idx) => <li key={idx}>• {i}</li>)}</ul>
          )}
        </SummaryRow>

        <SummaryRow icon={<Droplets className="w-4 h-4" />} label="Continence">
          {summary.continence?.summary || 'See care plan'}
        </SummaryRow>

        <SummaryRow icon={<MessageSquare className="w-4 h-4" />} label="Communication">
          {summary.communication?.summary || 'Standard communication'}
          {summary.communication?.strategies?.length > 0 && (
            <ul className="mt-1 text-xs text-gray-600">{summary.communication.strategies.map((s, i) => <li key={i}>• {s}</li>)}</ul>
          )}
        </SummaryRow>

        <SummaryRow icon={<Coffee className="w-4 h-4" />} label="Preferences">
          {summary.preferences?.length > 0 ? summary.preferences.join(' · ') : 'See full care plan'}
        </SummaryRow>

        <SummaryRow icon={<Pill className="w-4 h-4" />} label="Key Medications">
          {summary.keyMeds?.alerts?.length > 0 && summary.keyMeds.alerts.map((a, i) => (
            <span key={i} className="inline-block px-2 py-0.5 bg-orange-100 text-orange-800 text-xs rounded mr-1 mb-1">{a}</span>
          ))}
          {summary.keyMeds?.interventions?.length > 0 && (
            <ul className="mt-1 text-xs text-gray-600">{summary.keyMeds.interventions.map((i, idx) => <li key={idx}>• {i}</li>)}</ul>
          )}
        </SummaryRow>

        <SummaryRow icon={<Target className="w-4 h-4" />} label="Current Goals">
          {summary.goals?.length > 0
            ? <ul className="text-xs">{summary.goals.map((g, i) => <li key={i}>• {g}</li>)}</ul>
            : 'See full care plan'}
        </SummaryRow>

        <div className="mt-3 text-xs text-gray-400 flex justify-between">
          <span>Last updated: {summary.lastUpdated ? new Date(summary.lastUpdated).toLocaleDateString() : 'N/A'} by {summary.lastUpdatedBy || 'N/A'}</span>
          <span>Review due: {summary.reviewDueAt ? new Date(summary.reviewDueAt).toLocaleDateString() : 'N/A'}</span>
        </div>
      </div>
    </div>
  );
}

// ── CreatePlanModal ────────────────────────────────────────────────────

function CreatePlanModal({ residentId, onClose, onCreated }) {
  const [planType, setPlanType] = useState(CARE_PLAN_TYPE.COMPREHENSIVE);
  const [creating, setCreating] = useState(false);
  const [interimData, setInterimData] = useState({ allergies: '', diet: '', mobility: '', fallsRisk: 'medium', medications: '', communication: '', resuscitationStatus: '' });
  const [bspData, setBspData] = useState({ behaviours: '', triggers: '', strategies: '', unmetNeeds: '' });
  const [palData, setPalData] = useState({ resuscitationStatus: '', phaseOfIllness: 'stable', preferredPlaceOfDeath: '', anticipatoryMeds: '', comfortPriorities: '' });

  const handleCreate = async () => {
    setCreating(true);
    try {
      let result;
      switch (planType) {
        case CARE_PLAN_TYPE.INTERIM:
          result = CarePlanService.createInterimCarePlan(residentId, 'current-user', interimData);
          break;
        case CARE_PLAN_TYPE.BEHAVIOUR_SUPPORT:
          result = CarePlanService.createBehaviourSupportPlan(residentId, 'current-user', bspData);
          break;
        case CARE_PLAN_TYPE.PALLIATIVE:
          result = CarePlanService.createPalliativeCarePlan(residentId, 'current-user', palData);
          break;
        default:
          result = CarePlanService.createCarePlanRecord(residentId, 'current-user', planType);
      }
      if (result.ok) {
        log('Created care plan', result.carePlan.id, planType);
        onCreated(result.carePlan);
        onClose();
      }
    } finally {
      setCreating(false);
    }
  };

  const handleAutoGenerate = async () => {
    setCreating(true);
    try {
      const result = CarePlanService.generateFromAssessments(residentId, 'current-user');
      if (result.ok) {
        log('Auto-generated care plan', result.carePlan.id, `${result.elapsedMs}ms`);
        onCreated(result.carePlan);
        onClose();
      } else {
        log('Auto-generate failed:', result.error);
        alert(result.error === 'no_assessment_instances' ? 'No completed assessments found for this resident. Complete assessments first.' : result.error);
      }
    } finally {
      setCreating(false);
    }
  };

  const InputField = ({ label, value, onChange, placeholder, textarea }) => (
    <div>
      <label className="text-xs font-medium text-gray-600">{label}</label>
      {textarea ? (
        <textarea value={value} onChange={onChange} placeholder={placeholder} rows={2}
          className="w-full mt-1 text-sm px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
      ) : (
        <input value={value} onChange={onChange} placeholder={placeholder}
          className="w-full mt-1 text-sm px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold text-gray-800">Create New Care Plan</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-5">
          {/* Plan type selector */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">Care Plan Type</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {Object.entries(TYPE_LABELS).filter(([k]) => k !== CARE_PLAN_TYPE.SUMMARY).map(([type, label]) => (
                <button key={type} onClick={() => setPlanType(type)}
                  className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${planType === type ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 hover:border-gray-300'}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Auto-generate button for comprehensive */}
          {planType === CARE_PLAN_TYPE.COMPREHENSIVE && (
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg"><RefreshCw className="w-5 h-5 text-purple-600" /></div>
                <div className="flex-1">
                  <h4 className="font-semibold text-purple-900">Auto-Generate from Assessments</h4>
                  <p className="text-xs text-purple-600">Populate all 18 sections from completed assessments in seconds</p>
                </div>
                <button onClick={handleAutoGenerate} disabled={creating}
                  className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2">
                  {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Generate
                </button>
              </div>
            </div>
          )}

          {/* Type-specific fields */}
          {planType === CARE_PLAN_TYPE.INTERIM && (
            <div className="space-y-3">
              <h4 className="font-semibold text-gray-700 text-sm">Immediate Safety Information (Day 0)</h4>
              <div className="grid grid-cols-2 gap-3">
                <InputField label="Allergies" value={interimData.allergies} onChange={e => setInterimData(p => ({ ...p, allergies: e.target.value }))} placeholder="Drug, food, environmental..." />
                <InputField label="Diet" value={interimData.diet} onChange={e => setInterimData(p => ({ ...p, diet: e.target.value }))} placeholder="Diet type, IDDSI level..." />
                <InputField label="Mobility" value={interimData.mobility} onChange={e => setInterimData(p => ({ ...p, mobility: e.target.value }))} placeholder="Walking aid, transfer method..." />
                <div>
                  <label className="text-xs font-medium text-gray-600">Falls Risk</label>
                  <select value={interimData.fallsRisk} onChange={e => setInterimData(p => ({ ...p, fallsRisk: e.target.value }))}
                    className="w-full mt-1 text-sm px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500">
                    <option value="low">Low</option><option value="medium">Medium</option>
                    <option value="high">High</option><option value="very_high">Very High</option>
                  </select>
                </div>
                <InputField label="Medications" value={interimData.medications} onChange={e => setInterimData(p => ({ ...p, medications: e.target.value }))} placeholder="Critical medications..." />
                <InputField label="Communication" value={interimData.communication} onChange={e => setInterimData(p => ({ ...p, communication: e.target.value }))} placeholder="Language, hearing, vision..." />
              </div>
              <InputField label="Resuscitation Status" value={interimData.resuscitationStatus} onChange={e => setInterimData(p => ({ ...p, resuscitationStatus: e.target.value }))} placeholder="e.g., NFR, For Resuscitation" />
            </div>
          )}

          {planType === CARE_PLAN_TYPE.BEHAVIOUR_SUPPORT && (
            <div className="space-y-3">
              <h4 className="font-semibold text-gray-700 text-sm">Behaviour Support Details</h4>
              <InputField label="Behaviours Observed" value={bspData.behaviours} onChange={e => setBspData(p => ({ ...p, behaviours: e.target.value }))} placeholder="Types, frequency, severity..." textarea />
              <InputField label="Identified Triggers" value={bspData.triggers} onChange={e => setBspData(p => ({ ...p, triggers: e.target.value }))} placeholder="What triggers the behaviours..." textarea />
              <InputField label="De-escalation Strategies" value={bspData.strategies} onChange={e => setBspData(p => ({ ...p, strategies: e.target.value }))} placeholder="What works for this resident..." textarea />
              <InputField label="Unmet Needs Analysis" value={bspData.unmetNeeds} onChange={e => setBspData(p => ({ ...p, unmetNeeds: e.target.value }))} placeholder="Pain? Boredom? Fear? Toileting?" textarea />
            </div>
          )}

          {planType === CARE_PLAN_TYPE.PALLIATIVE && (
            <div className="space-y-3">
              <h4 className="font-semibold text-gray-700 text-sm">Palliative / End-of-Life Details</h4>
              <InputField label="Resuscitation Status" value={palData.resuscitationStatus} onChange={e => setPalData(p => ({ ...p, resuscitationStatus: e.target.value }))} placeholder="NFR, Comfort measures only..." />
              <div>
                <label className="text-xs font-medium text-gray-600">Phase of Illness</label>
                <select value={palData.phaseOfIllness} onChange={e => setPalData(p => ({ ...p, phaseOfIllness: e.target.value }))}
                  className="w-full mt-1 text-sm px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500">
                  <option value="stable">Stable</option><option value="unstable">Unstable</option>
                  <option value="deteriorating">Deteriorating</option><option value="terminal">Terminal</option>
                </select>
              </div>
              <InputField label="Preferred Place of Death" value={palData.preferredPlaceOfDeath} onChange={e => setPalData(p => ({ ...p, preferredPlaceOfDeath: e.target.value }))} placeholder="This facility, hospital, home..." />
              <InputField label="Anticipatory Medications" value={palData.anticipatoryMeds} onChange={e => setPalData(p => ({ ...p, anticipatoryMeds: e.target.value }))} placeholder="Prescribed and available?" textarea />
              <InputField label="Comfort Priorities" value={palData.comfortPriorities} onChange={e => setPalData(p => ({ ...p, comfortPriorities: e.target.value }))} placeholder="Music, touch, positioning, familiar voices..." textarea />
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 p-6 border-t bg-gray-50 rounded-b-2xl">
          <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-lg">Cancel</button>
          <button onClick={handleCreate} disabled={creating}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2">
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            {planType === CARE_PLAN_TYPE.COMPREHENSIVE ? 'Create Blank Plan' : `Create ${TYPE_LABELS[planType]} Plan`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main CarePlans Component ────────────────────────────────────────────

const CarePlans = ({ residentId }) => {
  const [activeTab, setActiveTab] = useState('plans');
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [expandedSections, setExpandedSections] = useState(new Set());
  const [editingSection, setEditingSection] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [summaryData, setSummaryData] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadPlans = useCallback(() => {
    setLoading(true);
    try {
      seedCarePlans();

      const loaded = residentId
        ? CarePlanService.getCarePlansByResident(residentId)
        : CarePlanService._getAll().sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setPlans(loaded);
      log('Loaded', loaded.length, 'plans');

      const dashboard = CarePlanService.getFacilityDashboard();
      setDashboardData(dashboard);

      if (residentId && loaded.length > 0) {
        const activePlan = loaded.find(p => p.status === CARE_PLAN_STATUS.ACTIVE) || loaded[0];
        if (activePlan && !selectedPlan) {
          setSelectedPlan(activePlan);
          setActiveTab('detail');
        }
      }
    } finally {
      setLoading(false);
    }
  }, [residentId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadPlans(); }, [loadPlans]);

  const handlePlanCreated = useCallback((newPlan) => {
    loadPlans();
    setSelectedPlan(newPlan);
    setActiveTab('detail');
  }, [loadPlans]);

  const handleSelectPlan = useCallback((plan) => {
    setSelectedPlan(plan);
    setActiveTab('detail');
    setExpandedSections(new Set());
    setEditingSection(null);
  }, []);

  const handleSignOff = useCallback((planId) => {
    const result = CarePlanService.signOff(planId, 'current-user');
    if (result.ok) {
      log('Signed off', planId);
      setSelectedPlan(result.carePlan);
      loadPlans();
    }
  }, [loadPlans]);

  const handleStartReview = useCallback((planId) => {
    const result = CarePlanService.startReview(planId);
    if (result.ok) {
      setSelectedPlan(result.carePlan);
      loadPlans();
    }
  }, [loadPlans]);

  const handleCompleteReview = useCallback((planId) => {
    const result = CarePlanService.completeReview(planId, 'current-user', REVIEW_OUTCOME.MINOR_UPDATE);
    if (result.ok) {
      setSelectedPlan(result.carePlan);
      loadPlans();
    }
  }, [loadPlans]);

  const handleViewSummary = useCallback((planId) => {
    const result = CarePlanService.generateSummaryCarePlan(planId);
    if (result.ok) {
      setSummaryData(result.summary);
      setActiveTab('summary');
    }
  }, []);

  const toggleSection = (sectionNumber) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      next.has(sectionNumber) ? next.delete(sectionNumber) : next.add(sectionNumber);
      return next;
    });
  };

  const handleSectionSaved = (updatedPlan) => {
    setSelectedPlan(updatedPlan);
    setEditingSection(null);
    loadPlans();
  };

  const filteredPlans = useMemo(() => {
    return plans.filter(plan => {
      if (filterType !== 'all' && plan.type !== filterType) return false;
      if (filterStatus !== 'all' && plan.status !== filterStatus) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const matchesId = plan.id?.toLowerCase().includes(q);
        const matchesResident = String(plan.residentId).includes(q);
        return matchesId || matchesResident;
      }
      return true;
    });
  }, [plans, filterType, filterStatus, searchQuery]);

  const needsReview = useMemo(() => CarePlanService.getCarePlansNeedingReview(), [plans]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Render helpers ────────────────────────────────────────────────────

  const renderPlanCard = (plan) => {
    const sectionsFilled = plan.sections.filter(s => (s.needs?.length || 0) + (s.goals?.length || 0) + (s.interventions?.length || 0) > 0).length;
    const totalGoals = plan.sections.reduce((sum, s) => sum + (s.goals?.length || 0), 0);
    const totalInterventions = plan.sections.reduce((sum, s) => sum + (s.interventions?.length || 0), 0);
    const allAlerts = plan.sections.flatMap(s => s.clinicalAlerts || []);
    const isOverdue = plan.reviewDueAt && new Date(plan.reviewDueAt) < new Date();

    return (
      <div key={plan.id}
        onClick={() => handleSelectPlan(plan)}
        className={`bg-white rounded-xl shadow-sm border-l-4 ${TYPE_COLORS[plan.type] || 'border-gray-300'} p-5 cursor-pointer hover:shadow-md transition-shadow`}>
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-gray-800">{TYPE_LABELS[plan.type] || plan.type} Care Plan</h3>
              {isOverdue && <span className="px-1.5 py-0.5 bg-red-100 text-red-700 text-xs rounded font-medium">OVERDUE</span>}
            </div>
            <p className="text-xs text-gray-500 mt-0.5">v{plan.version} · {plan.id} · Resident {plan.residentId}</p>
          </div>
          <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[plan.status] || 'bg-gray-100'}`}>
            {plan.status?.replace(/_/g, ' ')}
          </span>
        </div>

        {allAlerts.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {allAlerts.slice(0, 3).map((alert, i) => (
              <span key={i} className="px-2 py-0.5 bg-red-50 text-red-700 text-xs rounded flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />{alert}
              </span>
            ))}
            {allAlerts.length > 3 && <span className="text-xs text-red-500">+{allAlerts.length - 3} more</span>}
          </div>
        )}

        <div className="grid grid-cols-4 gap-3 mb-3">
          <div className="text-center">
            <div className="text-lg font-bold text-blue-600">{sectionsFilled}</div>
            <div className="text-xs text-gray-500">/ 18 sections</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-green-600">{totalGoals}</div>
            <div className="text-xs text-gray-500">goals</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-teal-600">{totalInterventions}</div>
            <div className="text-xs text-gray-500">interventions</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-purple-600">{plan.version}</div>
            <div className="text-xs text-gray-500">version</div>
          </div>
        </div>

        <div className="flex items-center justify-between text-xs text-gray-500 pt-3 border-t">
          <span>Created {new Date(plan.createdAt).toLocaleDateString()}</span>
          {plan.signedOffBy && <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-green-500" />Signed off</span>}
          {plan.reviewDueAt && <span>Review: {new Date(plan.reviewDueAt).toLocaleDateString()}</span>}
        </div>
      </div>
    );
  };

  const renderDashboard = () => {
    if (!dashboardData) return null;
    const d = dashboardData;
    const kpis = [
      { label: 'Active Plans', value: d.totalActive, icon: <FileText className="w-5 h-5" />, color: 'blue' },
      { label: 'Current (%)', value: `${d.carePlansCurrent}%`, icon: <CheckCircle className="w-5 h-5" />, color: d.carePlansCurrent >= 90 ? 'green' : 'yellow', target: '100%' },
      { label: 'Overdue Reviews', value: d.carePlansOverdue, icon: <AlertTriangle className="w-5 h-5" />, color: d.carePlansOverdue === 0 ? 'green' : 'red', target: '0' },
      { label: 'Avg Days Since Review', value: d.avgDaysSinceReview, icon: <Calendar className="w-5 h-5" />, color: d.avgDaysSinceReview <= 56 ? 'green' : 'yellow', target: '<56' },
      { label: 'Reviews This Week', value: d.reviewsThisWeek, icon: <ClipboardCheck className="w-5 h-5" />, color: 'blue' },
      { label: 'Goals Achieved', value: d.goalsAchieved, icon: <Award className="w-5 h-5" />, color: 'green' },
      { label: 'Goals At Risk', value: d.goalsNotAchieved, icon: <XCircle className="w-5 h-5" />, color: d.goalsNotAchieved > 0 ? 'red' : 'green' },
      { label: 'Interim >48h', value: d.interimStillActiveOver48h, icon: <Clock className="w-5 h-5" />, color: d.interimStillActiveOver48h === 0 ? 'green' : 'red', target: '0' },
    ];
    const colorMap = { blue: 'from-blue-500 to-blue-600', green: 'from-green-500 to-green-600', yellow: 'from-yellow-500 to-yellow-600', red: 'from-red-500 to-red-600' };

    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {kpis.map((kpi, i) => (
          <div key={i} className="bg-white rounded-xl shadow-sm p-4">
            <div className="flex items-center justify-between mb-2">
              <div className={`p-2 rounded-lg bg-gradient-to-br ${colorMap[kpi.color]} text-white`}>{kpi.icon}</div>
              {kpi.target && <span className="text-xs text-gray-400">Target: {kpi.target}</span>}
            </div>
            <div className="text-2xl font-bold text-gray-800">{kpi.value}</div>
            <div className="text-xs text-gray-500">{kpi.label}</div>
          </div>
        ))}
      </div>
    );
  };

  const renderPlanDetail = () => {
    if (!selectedPlan) return <div className="text-center text-gray-500 py-12">Select a care plan to view</div>;
    const plan = selectedPlan;
    const isEditable = [CARE_PLAN_STATUS.DRAFT, CARE_PLAN_STATUS.UNDER_REVIEW].includes(plan.status);

    return (
      <div className="space-y-4">
        {/* Plan header */}
        <div className="bg-white rounded-xl shadow-sm p-5">
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-bold text-gray-800">{TYPE_LABELS[plan.type]} Care Plan</h2>
                <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[plan.status]}`}>{plan.status?.replace(/_/g, ' ')}</span>
              </div>
              <p className="text-sm text-gray-500 mt-1">Version {plan.version} · Resident {plan.residentId} · Created {new Date(plan.createdAt).toLocaleDateString()}</p>
              {plan.signedOffBy && <p className="text-xs text-green-600 mt-1">Signed off by {plan.signedOffBy} on {new Date(plan.signedOffAt).toLocaleString()}</p>}
              {plan.generatedFrom === 'auto_assessment' && <p className="text-xs text-purple-600 mt-1">Auto-generated from {plan.assessmentInstanceIds?.length || 0} assessments</p>}
            </div>
            <div className="flex gap-2">
              {plan.status === CARE_PLAN_STATUS.DRAFT && (
                <button onClick={() => handleSignOff(plan.id)} className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" /> Sign Off
                </button>
              )}
              {plan.status === CARE_PLAN_STATUS.ACTIVE && (
                <>
                  <button onClick={() => handleViewSummary(plan.id)} className="px-3 py-2 text-teal-600 hover:bg-teal-50 text-sm rounded-lg flex items-center gap-2">
                    <Eye className="w-4 h-4" /> Summary View
                  </button>
                  <button onClick={() => handleStartReview(plan.id)} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 flex items-center gap-2">
                    <RefreshCw className="w-4 h-4" /> Start Review
                  </button>
                </>
              )}
              {plan.status === CARE_PLAN_STATUS.UNDER_REVIEW && (
                <button onClick={() => handleCompleteReview(plan.id)} className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" /> Complete Review
                </button>
              )}
              <button onClick={() => { setSelectedPlan(null); setActiveTab('plans'); }} className="px-3 py-2 text-gray-500 hover:bg-gray-100 rounded-lg">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Review info bar */}
          <div className="flex gap-4 text-xs bg-gray-50 rounded-lg p-3">
            <div><span className="text-gray-500">Review due:</span> <span className="font-medium">{plan.reviewDueAt ? new Date(plan.reviewDueAt).toLocaleDateString() : 'N/A'}</span></div>
            <div><span className="text-gray-500">Last reviewed:</span> <span className="font-medium">{plan.lastReviewedAt ? new Date(plan.lastReviewedAt).toLocaleDateString() : 'Never'}</span></div>
            {plan.lastReviewedBy && <div><span className="text-gray-500">By:</span> <span className="font-medium">{plan.lastReviewedBy}</span></div>}
            {plan.reviewOutcome && <div><span className="text-gray-500">Outcome:</span> <span className="font-medium">{plan.reviewOutcome.replace(/_/g, ' ')}</span></div>}
          </div>
        </div>

        {/* 18 Sections accordion */}
        <div className="space-y-2">
          {plan.sections.map(section => {
            const isExpanded = expandedSections.has(section.sectionNumber);
            const isEditing = editingSection === section.sectionNumber;
            const itemCount = (section.needs?.length || 0) + (section.goals?.length || 0) + (section.interventions?.length || 0);
            const hasAlerts = (section.clinicalAlerts || []).length > 0;
            const secDef = CARE_PLAN_SECTIONS.find(s => s.number === section.sectionNumber);

            return (
              <div key={section.sectionNumber} className={`bg-white rounded-xl shadow-sm border ${hasAlerts ? 'border-red-200' : 'border-gray-100'}`}>
                <button onClick={() => toggleSection(section.sectionNumber)}
                  className="w-full flex items-center justify-between p-4 hover:bg-gray-50 rounded-xl transition-colors">
                  <div className="flex items-center gap-3">
                    {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                    <div className="text-blue-600">{SECTION_ICONS[section.sectionNumber]}</div>
                    <div className="text-left">
                      <h4 className="font-medium text-gray-800 text-sm">
                        {section.sectionNumber}. {section.title}
                      </h4>
                      <p className="text-xs text-gray-500">{secDef?.standard ? `Standard ${secDef.standard}` : ''}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {hasAlerts && <AlertTriangle className="w-4 h-4 text-red-500" />}
                    <span className={`px-2 py-0.5 rounded text-xs ${RISK_COLORS[section.riskLevel] || 'bg-gray-100'}`}>
                      {section.riskLevel?.replace(/_/g, ' ') || 'low'}
                    </span>
                    {itemCount > 0 && <span className="text-xs text-gray-400">{itemCount} items</span>}
                    <span className={`px-1.5 py-0.5 rounded text-xs ${section.status === SECTION_STATUS.REVIEWED ? 'bg-green-100 text-green-700' : section.status === SECTION_STATUS.AMENDED ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
                      {section.status?.replace(/_/g, ' ')}
                    </span>
                  </div>
                </button>

                {isExpanded && (
                  <div className="px-4 pb-4 border-t">
                    {isEditing ? (
                      <div className="pt-3">
                        <SectionEditor section={section} carePlanId={plan.id} onSave={handleSectionSaved} onCancel={() => setEditingSection(null)} readOnly={false} />
                      </div>
                    ) : (
                      <div className="pt-3">
                        <SectionEditor section={section} carePlanId={plan.id} onSave={handleSectionSaved} onCancel={() => {}} readOnly={true} />
                        {isEditable && (
                          <div className="mt-3 pt-3 border-t">
                            <button onClick={() => setEditingSection(section.sectionNumber)}
                              className="px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg flex items-center gap-2">
                              <Edit3 className="w-4 h-4" /> Edit Section
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const activePlanStats = useMemo(() => {
    const active = plans.find(p => p.status === CARE_PLAN_STATUS.ACTIVE && p.type === CARE_PLAN_TYPE.COMPREHENSIVE);
    if (!active) return null;
    const allNeeds = active.sections.flatMap(s => s.needs || []);
    const allGoals = active.sections.flatMap(s => s.goals || []);
    const allIntvs = active.sections.flatMap(s => s.interventions || []);
    const filledSections = active.sections.filter(s => (s.needs?.length || 0) + (s.goals?.length || 0) + (s.interventions?.length || 0) > 0).length;
    const allAlerts = active.sections.flatMap(s => s.clinicalAlerts || []);
    const goalsAchieved = allGoals.filter(g => g.status === GOAL_STATUS.ACHIEVED).length;
    const goalsInProgress = allGoals.filter(g => g.status === GOAL_STATUS.IN_PROGRESS).length;
    const isOverdue = active.reviewDueAt && new Date(active.reviewDueAt) < new Date();
    return { plan: active, allNeeds, allGoals, allIntvs, filledSections, allAlerts, goalsAchieved, goalsInProgress, isOverdue };
  }, [plans]);

  // ── Main render ───────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Header */}
      {residentId ? (
        <div className="bg-gradient-to-r from-teal-600 to-blue-700 rounded-2xl p-6 text-white shadow-xl">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <FileText className="w-7 h-7" />
              <div>
                <h2 className="text-xl font-bold">Care Plan & Activities</h2>
                <p className="text-teal-100 text-sm">
                  {activePlanStats ? `v${activePlanStats.plan.version} · ${activePlanStats.filledSections}/18 sections · Last reviewed ${activePlanStats.plan.lastReviewedAt ? new Date(activePlanStats.plan.lastReviewedAt).toLocaleDateString() : 'Never'}` : 'No active care plan'}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              {activePlanStats?.isOverdue && (
                <span className="flex items-center gap-1 px-3 py-1.5 bg-red-500 bg-opacity-30 rounded-lg text-sm font-medium">
                  <AlertTriangle className="w-4 h-4" /> Review Overdue
                </span>
              )}
              <button onClick={() => setShowCreateModal(true)}
                className="bg-white text-teal-700 px-4 py-2 rounded-xl font-semibold hover:bg-teal-50 flex items-center gap-2 text-sm">
                <Plus className="w-4 h-4" /> New Plan
              </button>
            </div>
          </div>
          {activePlanStats && (
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
              <div className="bg-white/15 rounded-xl px-3 py-2 backdrop-blur">
                <div className="text-xs text-teal-100/80">Needs</div>
                <div className="text-xl font-bold">{activePlanStats.allNeeds.length}</div>
              </div>
              <div className="bg-white/15 rounded-xl px-3 py-2 backdrop-blur">
                <div className="text-xs text-teal-100/80">Goals</div>
                <div className="text-xl font-bold">{activePlanStats.allGoals.length}</div>
              </div>
              <div className="bg-white/15 rounded-xl px-3 py-2 backdrop-blur">
                <div className="text-xs text-teal-100/80">Interventions</div>
                <div className="text-xl font-bold">{activePlanStats.allIntvs.length}</div>
              </div>
              <div className="bg-white/15 rounded-xl px-3 py-2 backdrop-blur">
                <div className="text-xs text-teal-100/80">Goals Achieved</div>
                <div className="text-xl font-bold text-green-300">{activePlanStats.goalsAchieved}/{activePlanStats.allGoals.length}</div>
              </div>
              <div className="bg-white/15 rounded-xl px-3 py-2 backdrop-blur">
                <div className="text-xs text-teal-100/80">Alerts</div>
                <div className="text-xl font-bold text-yellow-300">{activePlanStats.allAlerts.length}</div>
              </div>
              <div className="bg-white/15 rounded-xl px-3 py-2 backdrop-blur">
                <div className="text-xs text-teal-100/80">Next Review</div>
                <div className="text-sm font-bold">{activePlanStats.plan.reviewDueAt ? new Date(activePlanStats.plan.reviewDueAt).toLocaleDateString() : 'N/A'}</div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-gradient-to-r from-blue-600 to-purple-700 rounded-2xl p-6 text-white shadow-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileText className="w-8 h-8" />
              <div>
                <h1 className="text-2xl font-bold">Care Plans Management</h1>
                <p className="text-blue-100 text-sm mt-1">
                  5 plan types · 18 clinical sections · Evidence-based interventions · 8-weekly review lifecycle
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              {needsReview.length > 0 && (
                <span className="flex items-center gap-1 px-3 py-1.5 bg-yellow-500 bg-opacity-20 rounded-lg text-sm">
                  <Bell className="w-4 h-4" /> {needsReview.length} due for review
                </span>
              )}
              <button onClick={() => setShowCreateModal(true)}
                className="bg-white text-blue-600 px-4 py-2 rounded-xl font-semibold hover:bg-opacity-90 flex items-center gap-2">
                <Plus className="w-5 h-5" /> New Care Plan
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm">
        <div className="border-b">
          <nav className="flex px-6 gap-1">
            {[
              { key: 'plans', label: `All Plans (${filteredPlans.length})`, icon: <FileText className="w-4 h-4" /> },
              { key: 'detail', label: 'Plan Detail', icon: <Clipboard className="w-4 h-4" />, hidden: !selectedPlan },
              { key: 'summary', label: 'Summary View', icon: <Eye className="w-4 h-4" />, hidden: !summaryData },
              { key: 'dashboard', label: 'Dashboard', icon: <BarChart3 className="w-4 h-4" />, hidden: !!residentId },
              { key: 'goals', label: 'Goal Tracking', icon: <Target className="w-4 h-4" /> },
              { key: 'reviews', label: `Reviews Due (${needsReview.length})`, icon: <RefreshCw className="w-4 h-4" /> },
            ].filter(t => !t.hidden).map(tab => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                className={`py-3 px-4 text-sm font-medium border-b-2 flex items-center gap-2 transition-colors ${
                  activeTab === tab.key ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}>
                {tab.icon} {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            </div>
          ) : (
            <>
              {activeTab === 'plans' && (
                <div>
                  {/* Search & filter bar */}
                  <div className="flex gap-3 mb-5">
                    <div className="flex-1 relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search plans by ID or resident..."
                        className="w-full pl-9 pr-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                    </div>
                    <select value={filterType} onChange={e => setFilterType(e.target.value)}
                      className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
                      <option value="all">All Types</option>
                      {Object.entries(TYPE_LABELS).map(([val, label]) => <option key={val} value={val}>{label}</option>)}
                    </select>
                    <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
                      className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
                      <option value="all">All Status</option>
                      {Object.values(CARE_PLAN_STATUS).map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
                    </select>
                  </div>

                  {filteredPlans.length === 0 ? (
                    <div className="text-center py-12">
                      <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <h3 className="text-lg font-semibold text-gray-600">No care plans found</h3>
                      <p className="text-sm text-gray-500 mt-1">Create a new care plan or adjust your filters</p>
                      <button onClick={() => setShowCreateModal(true)} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">
                        <Plus className="w-4 h-4 inline mr-1" /> Create Care Plan
                      </button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {filteredPlans.map(renderPlanCard)}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'detail' && renderPlanDetail()}

              {activeTab === 'summary' && (
                <div className="max-w-lg mx-auto">
                  <SummaryView summary={summaryData} />
                </div>
              )}

              {activeTab === 'dashboard' && (
                <div>
                  {renderDashboard()}
                  {dashboardData?.sectionsWithIncreasedRisk?.length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-4 mt-4">
                      <h4 className="font-semibold text-red-800 mb-2 flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Sections with Increased Risk</h4>
                      {dashboardData.sectionsWithIncreasedRisk.map((item, i) => (
                        <div key={i} className="text-sm text-red-700">Resident {item.residentId} — {item.sectionTitle}</div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'goals' && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Goal Tracking</h3>
                  {plans.filter(p => p.status === CARE_PLAN_STATUS.ACTIVE).length === 0 ? (
                    <p className="text-gray-500">No active care plans with goals.</p>
                  ) : (
                    plans.filter(p => p.status === CARE_PLAN_STATUS.ACTIVE).map(plan => {
                      const goals = CarePlanService.getAllGoals(plan.id);
                      if (goals.length === 0) return null;
                      return (
                        <div key={plan.id} className="mb-6">
                          <h4 className="font-medium text-gray-700 mb-2">{TYPE_LABELS[plan.type]} — Resident {plan.residentId} (v{plan.version})</h4>
                          <div className="space-y-2">
                            {goals.map(goal => (
                              <div key={goal.id} className="bg-white border rounded-lg p-3 flex items-start justify-between gap-3">
                                <div className="flex-1">
                                  <p className="text-sm">{goal.goalText}</p>
                                  <p className="text-xs text-gray-500 mt-1">Section {goal.sectionNumber}: {goal.sectionTitle}</p>
                                  {goal.progressEntries?.length > 0 && (
                                    <p className="text-xs text-blue-500 mt-1">{goal.progressEntries.length} progress entries — last: {goal.progressEntries[goal.progressEntries.length - 1]?.progressRating}</p>
                                  )}
                                </div>
                                <span className={`px-2 py-0.5 rounded text-xs whitespace-nowrap ${GOAL_STATUS_COLORS[goal.status] || 'bg-gray-100'}`}>
                                  {(goal.status || 'not_started').replace(/_/g, ' ')}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}

              {activeTab === 'reviews' && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Care Plans Due for Review</h3>
                  {needsReview.length === 0 ? (
                    <div className="text-center py-8">
                      <CheckCircle className="w-10 h-10 text-green-400 mx-auto mb-2" />
                      <p className="text-gray-500">All care plan reviews are current</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {needsReview.map(plan => (
                        <div key={plan.id} className="bg-white border border-yellow-200 rounded-lg p-4 flex items-center justify-between">
                          <div>
                            <h4 className="font-medium text-gray-800">{TYPE_LABELS[plan.type]} — Resident {plan.residentId}</h4>
                            <p className="text-sm text-gray-500">Review due: {new Date(plan.reviewDueAt).toLocaleDateString()} · Last reviewed: {plan.lastReviewedAt ? new Date(plan.lastReviewedAt).toLocaleDateString() : 'Never'}</p>
                          </div>
                          <button onClick={() => { handleSelectPlan(plan); handleStartReview(plan.id); }}
                            className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 flex items-center gap-2">
                            <RefreshCw className="w-4 h-4" /> Start Review
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {showCreateModal && (
        <CreatePlanModal residentId={residentId || '1'} onClose={() => setShowCreateModal(false)} onCreated={handlePlanCreated} />
      )}
    </div>
  );
};

export default CarePlans;
